import Phaser, { Game } from "phaser"
import "./styles.css"
import BBCodeTextPlugin from 'phaser3-rex-plugins/plugins/bbcodetext-plugin'
import CheckboxPlugin from 'phaser3-rex-plugins/plugins/checkbox-plugin'

// Import all your scenes
import LogoScene from "./scenes/logoScene"
import { updateScaling } from "./config/scaling"
import LoadingScene from "./scenes/loadingScene"
import StartScene from "./scenes/startScene"
import LoginScene from "./scenes/loginScene"
import StoryIntroScene from "./scenes/storyIntroScene"
import Customization from "./scenes/customization"
import PlanetSelector from "./scenes/planetSelector"
import LandingScene from "./scenes/landingScene"
import CockpitScene from "./scenes/cockpitScene"
import TabletScene from "./scenes/tabletScene"
import CreatureDatabase from "./scenes/creatureDatabase"
import FieldGuide from "./scenes/fieldGuide"
import EmailScene from "./scenes/email"
import AlertsScene from "./scenes/alerts"
import RadarScene from "./scenes/radarScene"
import MissionOutro from "./scenes/missionOutro"
import HelpDeskScene from "./scenes/helpDesk"
import CertificatePresentation from "./scenes/certificatePresentation"
import GameUI from "./helpers/gameUI"
import SoundManager from "./managers/sound-manager"
import { GameAnalytics } from "gameanalytics"

// Import helpers
import ScrollPanel from "./helpers/scrollPanel"
import { API_CONFIG } from "./config/apiConfig"

import { createPlanetScroller } from "./helpers/planetScrollerFunction"
import TabletViewUI from "./helpers/tabletViewUI"
import OrientationCheck from "./config/orientationCheck"
import LevelManager from "./helpers/levelManager"
import showFeedingTrayFunction from "./helpers/cockpitFunctions/showFeedingTrayFunction"
import { feedingFunctionality } from "./helpers/cockpitFunctions/feedingFunctionalityFunction"
import MediclinicAPI from "./helpers/apiHandler"

// FULLSCREEN ENFORCEMENT TOGGLE - Set to false to disable fullscreen requirement
const ENFORCE_FULLSCREEN = true

// Track keyboard visibility on iOS
let keyboardVisible = false

function isLandscape() {
	// More reliable landscape check that waits for actual dimensions
	const width = window.visualViewport ? window.visualViewport.width : window.innerWidth;
	const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
	return width > height;
}

function isInFullscreen() {
	// Simple check - is there a fullscreen element?
	return !!(
		document.fullscreenElement ||
		document.webkitFullscreenElement ||
		document.mozFullScreenElement ||
		document.msFullscreenElement
	);
}

// Setup keyboard detection for iOS
function setupKeyboardDetection() {
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

	if (!isIOS) return;

	// Detect keyboard showing/hiding on iOS
	if (window.visualViewport) {
		window.visualViewport.addEventListener('resize', () => {
			const viewportHeight = window.visualViewport.height;
			const windowHeight = window.innerHeight;

			// If viewport is significantly smaller than window, keyboard is probably visible
			keyboardVisible = (windowHeight - viewportHeight) > 150;

			console.log('Keyboard visible:', keyboardVisible, 'ViewportHeight:', viewportHeight, 'WindowHeight:', windowHeight);
		});
	}
}

function updateGameDimensions() {
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
	const isEdge = /Edg/.test(navigator.userAgent);
	const isLegacyEdge = /Edge/.test(navigator.userAgent);

	let gameWidth, gameHeight;

	// Wait for landscape orientation on iOS
	if (isIOS && !isLandscape()) {
		console.log('Waiting for landscape orientation...');
		return null;
	}

	// For non-iOS devices with fullscreen enforcement, wait for fullscreen element
	if (!isIOS && ENFORCE_FULLSCREEN && !isInFullscreen()) {
		console.log('Waiting for fullscreen...');
		return null;
	}

	if (isIOS || !ENFORCE_FULLSCREEN) {
		if (window.visualViewport) {
			gameWidth = Math.round(window.visualViewport.width);
			gameHeight = Math.round(window.visualViewport.height);
		} else {
			gameWidth = Math.round(window.innerWidth);
			gameHeight = Math.round(window.innerHeight);
		}
	} else {
		// For fullscreen mode - with better handling for all browsers
		if (document.fullscreenElement) {
			// When in fullscreen, use screen dimensions
			gameWidth = Math.round(screen.width);
			gameHeight = Math.round(screen.height);
		} else if (isEdge || isLegacyEdge) {
			gameWidth = Math.round(screen.availWidth || window.innerWidth);
			gameHeight = Math.round(screen.availHeight || window.innerHeight);
		} else if (window.visualViewport) {
			gameWidth = Math.round(window.visualViewport.width);
			gameHeight = Math.round(window.visualViewport.height);
		} else {
			gameWidth = Math.round(window.innerWidth);
			gameHeight = Math.round(window.innerHeight);
		}
	}

	console.log(`Game dimensions: ${gameWidth}x${gameHeight} (Landscape: ${isLandscape()}, Fullscreen: ${isInFullscreen()}, Edge: ${isEdge || isLegacyEdge})`);
	return { gameWidth, gameHeight };
}

// Game configuration function
function createGameConfig() {
	const dimensions = updateGameDimensions();

	// Return null if dimensions aren't ready (not in landscape yet)
	if (!dimensions) {
		return null;
	}

	const { gameWidth, gameHeight } = dimensions;
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

	return {
		type: Phaser.WEBGL,
		backgroundColor: "#BECCFF",
		fps: { target: 60 },
		scale: {
			parent: "game",
			mode: Phaser.Scale.FIT,
			autoCenter: Phaser.Scale.CENTER_BOTH,
			width: gameWidth,
			height: gameHeight,
			fullscreenTarget: "game",
			lockOrientation: true,
			orientation: Phaser.Scale.LANDSCAPE,
			resolution: window.devicePixelRatio || 1,
			resizeInterval: 0,
			expandParent: false,
			autoRound: true,
		},
		render: {
			antialias: true,
			antialiasGL: true,
			pixelArt: false,
			roundPixels: true,
		},
		scene: [
			LogoScene,
			LoadingScene,
			StartScene,
			LoginScene,
			StoryIntroScene,
			Customization,
			PlanetSelector,
			LandingScene,
			CockpitScene,
			TabletScene,
			TabletViewUI,
			CreatureDatabase,
			FieldGuide,
			HelpDeskScene,
			OrientationCheck,
			EmailScene,
			AlertsScene,
			RadarScene,
			MissionOutro,
			CertificatePresentation,
			GameUI,
			SoundManager,
		],
		plugins: {
			global: [
				{
					key: 'rexBBCodeTextPlugin',
					plugin: BBCodeTextPlugin,
					start: true
				},
				{
					key: 'rexCheckboxPlugin',
					plugin: CheckboxPlugin,
					start: true
				}
			]
		},
		title: "MediMissions",
	}
}

// Variable to track game initialization
let game = null

// Function to refresh game dimensions when fullscreen changes
function refreshGameDimensions() {
	// Don't resize when keyboard is visible on iOS
	if (keyboardVisible) {
		console.log('Skipping refresh - keyboard visible');
		return;
	}

	if (game && game.scale) {
		const dimensions = updateGameDimensions();

		// Only update if we have valid dimensions
		if (!dimensions) {
			console.log('Skipping refresh - waiting for landscape');
			return;
		}

		const { gameWidth, gameHeight } = dimensions;

		// Update the game scale
		game.scale.setGameSize(gameWidth, gameHeight);

		// Force a refresh
		game.scale.refresh();

		// Additional refresh for Edge browsers
		if (/Edg|Edge/.test(navigator.userAgent)) {
			setTimeout(() => {
				game.scale.refresh();
			}, 100);
		}
	}
}

// Function to initialize the game when in landscape and fullscreen
function initializeGame() {
	const GAME_KEY = process.env.GAME_ANALYTICS_GAME_KEY;
	const SECRET_KEY = process.env.GAME_ANALYTICS_SECRET_KEY;

	const currentIsLandscape = isLandscape();
	const currentIsFullscreen = isInFullscreen();

	const isAndroidDevice = /android/i.test(navigator.userAgent);
	const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
	const gameContainer = document.getElementById('game');

	// Clear any existing classes
	gameContainer.classList.remove('ios-style', 'enforced-fullscreen');

	// Apply the appropriate class based on conditions
	if (isIOS || !ENFORCE_FULLSCREEN) {
		gameContainer.classList.add('ios-style');
	} else {
		gameContainer.classList.add('enforced-fullscreen');
	}

	const isMobileDevice = isAndroidDevice || isIOSDevice;

	// Check fullscreen requirements based on the toggle
	if (ENFORCE_FULLSCREEN) {
		if (
			(isAndroidDevice && (!currentIsLandscape || !currentIsFullscreen)) ||
			(isIOSDevice && !currentIsLandscape) ||
			(!isMobileDevice && !currentIsFullscreen)
		) {
			console.log('Not ready to initialize: Landscape:', currentIsLandscape, 'Fullscreen:', currentIsFullscreen);
			return null;
		}
	} else {
		if (!currentIsLandscape) {
			console.log('Not ready to initialize: waiting for landscape');
			return null;
		}
	}

	if (game) {
		game.destroy(true);
	}

	const gameConfig = createGameConfig();

	// Don't create game if config is null (dimensions not ready)
	if (!gameConfig) {
		console.log('Game config not ready - waiting for proper orientation and fullscreen');
		return null;
	}

	GameAnalytics.configureBuild("v1.0.0");
	GameAnalytics.setEnabledInfoLog(true);
	GameAnalytics.setEnabledVerboseLog(true);
	// GameAnalytics.initialize(GAME_KEY, SECRET_KEY)

	game = new Phaser.Game(gameConfig);

	// Hide top bar after game is created and first scene is loaded
	game.events.once("ready", () => {
		hideTopBar();
		// Refresh dimensions after game is ready with a delay to ensure everything settled
		setTimeout(refreshGameDimensions, 200);
	});

	// Add fullscreen change listener to refresh dimensions
	document.addEventListener("fullscreenchange", () => {
		refreshGameDimensions();
		// On Android, add an extra refresh after a short delay to ensure proper dimensions
		if (/android/i.test(navigator.userAgent)) {
			setTimeout(() => {
				refreshGameDimensions();
			}, 200);
		}
	});
	document.addEventListener("webkitfullscreenchange", () => {
		refreshGameDimensions();
		if (/android/i.test(navigator.userAgent)) {
			setTimeout(() => {
				refreshGameDimensions();
			}, 200);
		}
	});
	document.addEventListener("mozfullscreenchange", () => {
		refreshGameDimensions();
		if (/android/i.test(navigator.userAgent)) {
			setTimeout(() => {
				refreshGameDimensions();
			}, 200);
		}
	});
	document.addEventListener("MSFullscreenChange", () => {
		refreshGameDimensions();
		if (/android/i.test(navigator.userAgent)) {
			setTimeout(() => {
				refreshGameDimensions();
			}, 200);
		}
	});

	// Add resize listener for additional safety with debouncing
	let resizeTimeout;
	window.addEventListener("resize", () => {
		clearTimeout(resizeTimeout);
		resizeTimeout = setTimeout(refreshGameDimensions, 150);
	});

	// iOS specific: listen to visualViewport changes (but skip if keyboard is visible)
	if (isIOS && window.visualViewport) {
		window.visualViewport.addEventListener("resize", () => {
			// Only trigger resize if keyboard is not visible
			if (!keyboardVisible) {
				clearTimeout(resizeTimeout);
				resizeTimeout = setTimeout(refreshGameDimensions, 150);
			}
		});
	}

	// Edge-specific fixes
	if (/Edg|Edge/.test(navigator.userAgent)) {
		setTimeout(() => {
			refreshGameDimensions();
		}, 300);
	}

	setTimeout(hideTopBar, 1000);

	return game;
}

// Wait for fullscreen and landscape before initializing
function setupGameInitialization() {
	// Set global locale for DD/MM/YYYY date format
	document.documentElement.lang = "en-GB";
	document.documentElement.setAttribute("lang", "en-GB");

	// Setup keyboard detection for iOS
	setupKeyboardDetection();

	// GLOBAL iOS VISUAL VIEWPORT FIX
	function syncVisualViewport() {
		const vv = window.visualViewport
		if (!vv) return
		window._visualViewportOffsetTop = vv.offsetTop
		window._visualViewportOffsetLeft = vv.offsetLeft
		document.documentElement.style.setProperty("--vvh", `${vv.height}px`)
		document.documentElement.style.setProperty("--vvw", `${vv.width}px`)
		document.documentElement.style.setProperty("--vv-offset-top", `${vv.offsetTop}px`)
		document.documentElement.style.setProperty("--vv-offset-left", `${vv.offsetLeft}px`)
	}
	if (window.visualViewport) {
		window.visualViewport.addEventListener("resize", syncVisualViewport)
		window.visualViewport.addEventListener("scroll", syncVisualViewport)
	}
	window.addEventListener("resize", syncVisualViewport)
	syncVisualViewport()

	const isAndroid = /android/i.test(navigator.userAgent);
	const startedInLandscape = isLandscape();

	// Function to attempt game initialization with debouncing
	let initTimeout;
	function tryInitializeGame() {
		// Clear previous timeout to prevent multiple rapid initializations
		clearTimeout(initTimeout);

		// Wait 300ms for fullscreen transition to complete and dimensions to settle
		// Fullscreen API is asynchronous and takes time to update window dimensions
		initTimeout = setTimeout(() => {
			const gameInstance = initializeGame();
			if (gameInstance) {
				// If game successfully initialized, remove event listeners
				document.removeEventListener("fullscreenchange", tryInitializeGame);
				window.removeEventListener("resize", tryInitializeGame);
				window.removeEventListener("orientationchange", tryInitializeGame);
			}
		}, 300);
	}

	// Add event listeners to check for landscape and fullscreen
	if (ENFORCE_FULLSCREEN) {
		document.addEventListener("fullscreenchange", tryInitializeGame);
	}
	window.addEventListener("resize", tryInitializeGame);
	window.addEventListener("orientationchange", tryInitializeGame);

	// Initial attempt with delay to ensure page is fully loaded
	// On Android, if starting in landscape, add extra delay to ensure dimensions settle
	const initialDelay = (isAndroid && startedInLandscape) ? 500 : 100;
	setTimeout(tryInitializeGame, initialDelay);
}

function hideTopBar() {
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
	const isAndroid = /android/i.test(navigator.userAgent);

	if (isIOS) {
		setTimeout(() => {
			window.scrollTo(0, 1);
		}, 100);
	}

	if (isAndroid) {
		setTimeout(() => {
			window.scrollTo(0, 1);

			let viewport = document.querySelector('meta[name="viewport"]');
			if (viewport) {
				viewport.setAttribute(
					"content",
					"width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, minimal-ui",
				);
			}

			if (!ENFORCE_FULLSCREEN && document.fullscreenEnabled) {
				const gameContainer =
					document.getElementById("game") || document.documentElement;
				if (gameContainer.requestFullscreen) {
					if (!document.fullscreenElement) {
						gameContainer.requestFullscreen().catch((err) => {
							console.log("Fullscreen request failed:", err);
						});
					}
				} else if (gameContainer.webkitRequestFullscreen) {
					gameContainer.webkitRequestFullscreen();
				} else if (gameContainer.mozRequestFullScreen) {
					gameContainer.mozRequestFullScreen();
				} else if (gameContainer.msRequestFullscreen) {
					gameContainer.msRequestFullscreen();
				}
			}
		}, 100);
	}
}

// Start setup when page loads
window.addEventListener("load", setupGameInitialization);
